import * as React from 'react';
import { useId } from 'react';
import { cn } from '@/lib/utils';

interface BaseProps {
  label: string;
  error?: string | undefined;
  children?: React.ReactNode;
  className?: string | undefined;
  htmlFor?: string | undefined;
  /** Dica opcional ao lado do label (não-uppercase, atenuada) */
  hint?: React.ReactNode;
}

export function FieldLabel({
  children,
  htmlFor,
  className,
  hint,
}: {
  children: React.ReactNode;
  htmlFor?: string | undefined;
  className?: string | undefined;
  hint?: React.ReactNode;
}) {
  return (
    <label
      htmlFor={htmlFor}
      className={cn(
        'block mb-2 font-mono text-[12px] uppercase tracking-wider text-muted-foreground',
        className,
      )}
    >
      {children}
      {hint && <span className="ml-1.5 normal-case tracking-normal opacity-70">{hint}</span>}
    </label>
  );
}

export function FieldShell({ label, error, children, className, htmlFor, hint }: BaseProps) {
  return (
    <div className={cn('w-full', className)}>
      <FieldLabel htmlFor={htmlFor} hint={hint}>
        {label}
      </FieldLabel>
      {children}
      {error && <p className="text-destructive text-xs mt-1.5">{error}</p>}
    </div>
  );
}

interface StackedInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label: string;
  error?: string | undefined;
  /** Dica opcional ao lado do label */
  hint?: React.ReactNode;
  /** Sufixo mono à direita do input (ex: "R$") */
  suffix?: React.ReactNode;
}

export const StackedField = React.forwardRef<HTMLInputElement, StackedInputProps>(
  ({ label, error, hint, suffix, className, ...props }, ref) => {
    const id = useId();
    return (
      <FieldShell label={label} error={error} htmlFor={id} hint={hint}>
        <div className="relative">
          <input
            id={id}
            ref={ref}
            className={cn(
              // text-base (16px) evita zoom no iOS; foco escurece a borda
              'w-full rounded-lg border border-border bg-card px-3.5 py-3 text-base text-foreground outline-none transition-colors focus:border-foreground',
              suffix && 'pr-11',
              error && 'border-destructive',
              className,
            )}
            {...props}
          />
          {suffix && (
            <span className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 font-mono text-xs text-muted-foreground">
              {suffix}
            </span>
          )}
        </div>
      </FieldShell>
    );
  },
);
StackedField.displayName = 'StackedField';

interface StackedTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  error?: string | undefined;
  /** Dica opcional ao lado do label */
  hint?: React.ReactNode;
}

export const StackedTextarea = React.forwardRef<HTMLTextAreaElement, StackedTextareaProps>(
  ({ label, error, hint, className, ...props }, ref) => {
    const id = useId();
    return (
      <FieldShell label={label} error={error} htmlFor={id} hint={hint}>
        <textarea
          id={id}
          ref={ref}
          rows={3}
          className={cn(
            'w-full min-h-20 rounded-lg border border-border bg-card px-3.5 py-3 text-base text-foreground outline-none resize-y leading-snug transition-colors focus:border-foreground',
            error && 'border-destructive',
            className,
          )}
          {...props}
        />
      </FieldShell>
    );
  },
);
StackedTextarea.displayName = 'StackedTextarea';
